import React, { useMemo } from "react";

/**
 * ParticleField
 * Very sparse cyan dots drifting slowly in a deep hero background.
 * ~24 particles, elegant, non-busy. Absolute-positioned; parent must be relative.
 *
 * Props:
 *   count   – total dots (default 24)
 *   className – extra classes for the container
 */
export default function ParticleField({ count = 24, className = "" }) {
  // Generate stable random particles once per mount.
  const particles = useMemo(
    () =>
      Array.from({ length: count }, () => {
        const size = 1 + Math.random() * 2.2;        // 1 – 3.2 px
        const opacity = 0.3 + Math.random() * 0.6;   // 0.3 – 0.9
        const left = Math.random() * 100;            // 0–100 %
        const top = Math.random() * 100;             // 0–100 %
        const dx = (Math.random() - 0.5) * 60;       // ±30 px
        const dy = -(60 + Math.random() * 120);      // -60 → -180 px (drift up)
        const dur = 18 + Math.random() * 24;         // 18–42 s
        const delay = -Math.random() * 20;           // negative to desync starts
        const glow = Math.random() > 0.7;            // ~30% get a soft glow
        return { size, opacity, left, top, dx, dy, dur, delay, glow };
      }),
    [count]
  );

  return (
    <div
      aria-hidden="true"
      data-testid="particle-field"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {particles.map((p, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-cyan-300"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            opacity: p.opacity,
            boxShadow: p.glow
              ? `0 0 ${p.size * 3}px rgba(103, 232, 249, 0.7)`
              : undefined,
            animation: `particleDrift ${p.dur}s ${p.delay}s linear infinite`,
            // custom-properties consumed by keyframe
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
          }}
        />
      ))}
    </div>
  );
}
